import { useCallback, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import HorizontalLine from '../ui/HorizontalLine';
import StyledButton from '../styled/StyledButton';
import { ArrowRightIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import CalendarIcon from '@/assets/icons/calendar.svg?react';
import { format, parse, startOfToday } from 'date-fns';
import { Calendar } from '../ui/calendar';
import { cn } from '@/lib/utils';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { formatPrice } from '@/utils/circuits/prices/formatPrice';
import clsx from 'clsx';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { useTranslation } from '@/i18n';
import { useLanguage } from '@/contexts/LanguageContext';
import { LOCALES_MAP } from '@/constants/localeMap';

interface CircuitDetailsParticipantsCardProps {
  minimumPrice: number;
  nbAdults: number;
  setNbAdults: (callback: (value: number) => number) => void;
  nbChildren: number;
  setNbChildren: (callback: (value: number) => number) => void;
  childrenBirthDates: Record<number, Date>;
  setChildrenBirthDates: (
    callback: (value: Record<number, Date>) => Record<number, Date>,
  ) => void;
  defaultNbParticipants?: number;
  selectedArrivalDate?: Date | null;
  mainBtnLabel?: string;
  onSelectOptionsButtonClick?: () => void;
  shouldShowParticipantsSetterButtons?: boolean;
  displayDateSelector?: boolean;
  currency?: string;
  isViewMode?: boolean;
  secondaryBtnLabel?: string;
  onSecondaryBtnClick?: () => void;
  secondaryBtnDisabled?: boolean;
}

export const CircuitDetailsParticipantsCard = ({
  minimumPrice,
  nbAdults,
  setNbAdults,
  nbChildren,
  setNbChildren,
  childrenBirthDates,
  setChildrenBirthDates,
  defaultNbParticipants = 1,
  selectedArrivalDate = null,
  mainBtnLabel = 'Choisir mes options',
  onSelectOptionsButtonClick = () => {},
  shouldShowParticipantsSetterButtons = true,
  displayDateSelector = true,
  currency = 'EUR',
  isViewMode = false,
  secondaryBtnLabel,
  onSecondaryBtnClick = () => {},
  secondaryBtnDisabled = false,
}: CircuitDetailsParticipantsCardProps) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  return (
    <Card className="flex flex-col gap-4 bg-white border border-[#E2E8F0] rounded-lg lg:min-w-80 xl:min-w-96 h-fit">
      <CardHeader>
        <CardTitle className="flex flex-col gap-1">
          <span className="text-sm text-gray-600">
            {t('circuits.editorial.details.participantsCard.from')}
          </span>
          <h1 className="text-3xl font-bold text-brand-600">
            {formatPrice(minimumPrice)}
            {` `}
            {formatCurrency(currency)} TTC
          </h1>
          <span className="text-base text-brand-600">
            {t('circuits.editorial.details.participantsCard.perPerson')}
          </span>
        </CardTitle>
        <span className="text-xs text-gray-600">
          {t('circuits.editorial.details.participantsCard.priceBaseMessage', {
            participants: defaultNbParticipants,
          })}
        </span>
      </CardHeader>
      <HorizontalLine />
      <CardContent className="flex flex-col gap-4">
        <span className="text-sm text-gray-800 font-medium">
          {t('circuits.editorial.details.participantsCard.nbTravelers')}
        </span>
        <ParticipantsSetter
          nbParticipants={nbAdults}
          setNbParticipants={setNbAdults}
          label={t('circuits.editorial.details.participantsCard.adults')}
          isViewMode={isViewMode}
          shouldShowPlusMinusButtons={shouldShowParticipantsSetterButtons}
        />
        {(nbChildren > 0 || shouldShowParticipantsSetterButtons) && (
          <ParticipantsSetter
            nbParticipants={nbChildren}
            setNbParticipants={setNbChildren}
            label={t('circuits.editorial.details.participantsCard.children')}
            isViewMode={isViewMode}
            shouldShowPlusMinusButtons={shouldShowParticipantsSetterButtons}
          />
        )}
        {nbChildren > 0 && (
          <div className="flex flex-col gap-6 p-2 rounded-lg bg-brand-100">
            {Array.from({ length: nbChildren }).map((_, index) => (
              <ChildBirthDatePicker
                key={`child-birthdate-${index}`}
                index={index}
                childrenBirthDates={childrenBirthDates}
                setChildrenBirthDates={setChildrenBirthDates}
                isViewMode={isViewMode}
              />
            ))}
          </div>
        )}
        <HorizontalLine />
        <span className="text-sm font-medium text-gray-800">
          {t('circuits.editorial.details.participantsCard.departureDate')}
        </span>
        <StyledButton
          title={
            selectedArrivalDate
              ? format(selectedArrivalDate, 'PPPP', {
                  locale: LOCALES_MAP[currentLanguage],
                })
              : t(
                  'circuits.editorial.details.participantsCard.selectDepartureDate',
                )
          }
          className={clsx('flex justify-between px-6 py-3 rounded-lg', {
            'bg-brand-25 border-brand-600 text-black hover:bg-brand-100':
              selectedArrivalDate,
          })}
          icon={
            displayDateSelector ? <ArrowRightIcon className="size-5" /> : null
          }
          iconPosition="right"
          onClick={
            displayDateSelector
              ? () => window.location.replace('#calendar')
              : undefined
          }
        />
        <StyledButton
          title={mainBtnLabel}
          className="rounded-lg"
          disabled={!selectedArrivalDate}
          onClick={onSelectOptionsButtonClick}
        />
        {secondaryBtnLabel && (
          <StyledButton
            variant="outlined"
            title={secondaryBtnLabel}
            className="rounded-lg"
            onClick={onSecondaryBtnClick}
            disabled={secondaryBtnDisabled}
          />
        )}
      </CardContent>
    </Card>
  );
};

const ParticipantsSetter = ({
  nbParticipants,
  setNbParticipants,
  label,
  shouldShowPlusMinusButtons = true,
  isViewMode = false,
}: {
  nbParticipants: number;
  setNbParticipants: (callback: (prevNbParticipants: number) => number) => void;
  label: string;
  shouldShowPlusMinusButtons?: boolean;
  isViewMode?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex justify-between items-center">
      <span className="text-sm text-gray-800 font-medium">{label}</span>
      <div className="flex gap-6 items-center">
        {shouldShowPlusMinusButtons && (
          <StyledButton
            variant="link"
            className="p-0 border border-gray-300 size-8"
            title=""
            icon={<MinusIcon className="size-5" />}
            onClick={() =>
              setNbParticipants((prevNbParticipants) => prevNbParticipants - 1)
            }
            disabled={
              (label ===
                t('circuits.editorial.details.participantsCard.adults') &&
                nbParticipants === 1) ||
              nbParticipants === 0 ||
              isViewMode
            }
          />
        )}
        <span className="text-base text-black w-3 text-center">
          {nbParticipants}
        </span>
        {shouldShowPlusMinusButtons && (
          <StyledButton
            variant="link"
            className="p-0 border border-gray-300 size-8"
            title=""
            icon={<PlusIcon className="size-5" />}
            onClick={() =>
              setNbParticipants((prevNbParticipants) => prevNbParticipants + 1)
            }
            disabled={isViewMode}
          />
        )}
      </div>
    </div>
  );
};

const ChildBirthDatePicker = ({
  index,
  childrenBirthDates,
  setChildrenBirthDates,
  isViewMode = false,
}: {
  index: number;
  childrenBirthDates: Record<number, Date>;
  setChildrenBirthDates: (
    callback: (
      prevChildrenBirthDates: Record<number, Date>,
    ) => Record<number, Date>,
  ) => void;
  isViewMode?: boolean;
}) => {
  const { t } = useTranslation();
  const [stringDate, setStringDate] = useState<string>(
    childrenBirthDates[index]
      ? format(childrenBirthDates[index], 'dd/MM/yyyy')
      : '',
  );
  const [date, setDate] = useState<Date | undefined>(
    childrenBirthDates[index] ?? undefined,
  );

  const today = useMemo(() => startOfToday(), []);

  const onSelectCalendarDate = useCallback(
    (selectedDate: Date | undefined) => {
      if (!selectedDate) return;
      setDate(selectedDate);
      setStringDate(format(selectedDate, 'dd/MM/yyyy'));
      setChildrenBirthDates((prevChildrenBirthDates) => ({
        ...prevChildrenBirthDates,
        [index]: selectedDate,
      }));
    },
    [index, setChildrenBirthDates],
  );

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-gray-700">
        {t('circuits.editorial.details.participantsCard.child')} {index + 1} -{' '}
        {t('circuits.editorial.details.participantsCard.birthDate')}
      </Label>
      <Popover>
        <div className="relative w-full">
          <Input
            type="string"
            value={stringDate}
            placeholder={t(
              'circuits.editorial.details.participantsCard.birthDatePlaceholder',
            )}
            onChange={(e) => {
              setStringDate(e.target.value);
              const parsedDate = parse(
                e.target.value,
                'dd/MM/yyyy',
                new Date(),
              );
              if (parsedDate.toString() === 'Invalid Date') {
                setDate(undefined);
              } else {
                if (
                  parsedDate < today &&
                  parsedDate >= new Date('2000-01-01')
                ) {
                  setDate(parsedDate);
                  setChildrenBirthDates((prevChildrenBirthDates) => ({
                    ...prevChildrenBirthDates,
                    [index]: parsedDate,
                  }));
                } else {
                  setDate(undefined);
                }
              }
            }}
            disabled={isViewMode}
          />
          <PopoverTrigger asChild>
            <Button
              variant={'outline'}
              className={cn(
                'font-normal absolute right-0 translate-y-[-50%] top-[50%] rounded-l-none',
                !date && 'text-muted-foreground',
              )}
              disabled={isViewMode}
            >
              <CalendarIcon className="w-4 h-4 stroke-gray-500" />
            </Button>
          </PopoverTrigger>
        </div>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={onSelectCalendarDate}
            disabled={(date) => date >= today || date < new Date('2000-01-01')}
            defaultMonth={date ?? today}
            autoFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};
